/**
 * Formato de fechas y números **por locale y huso del tenant**.
 *
 * Ninguna vista debe llamar a `toLocaleDateString()` ni a `Intl` directamente:
 * ese camino usa el locale y el huso del navegador, que es exactamente lo que
 * la convención prohíbe (ver `tenant/settings.ts` para el porqué).
 *
 *     const { fmt } = useI18n();
 *     fmt.dateTime(alert.created_at)   // 26/07/2026 14:03 en el huso del tenant
 *     fmt.number(1234567)              // 1.234.567
 *     fmt.percent(0.0834)              // 8,3 %
 *
 * Los formateadores de `Intl` se construyen una vez por locale: instanciar un
 * `Intl.NumberFormat` por celda es medible en una tabla de mil filas.
 */

export interface Formatters {
  locale: string;
  timeZone: string;
  /** `26/07/2026`. */
  date(value: string | number | Date | null | undefined): string;
  /** `26/07/2026 14:03`. */
  dateTime(value: string | number | Date | null | undefined): string;
  /** `14:03`. */
  time(value: string | number | Date | null | undefined): string;
  /** `07/2026` — un vencimiento se lee como está impreso en el envase. */
  expiry(value: string | null | undefined): string;
  /** `hace 3 h`. Para "último escaneo" y timestamps de la bandeja. */
  relative(value: string | number | Date | null | undefined): string;
  /** `1.234.567`. */
  number(value: number | null | undefined, fractionDigits?: number): string;
  /** `1,2 M` — para contadores de unidades, donde el dígito exacto no aporta. */
  compact(value: number | null | undefined): string;
  /** `8,3 %`. Recibe la fracción (`0.083`), no `8.3`. */
  percent(value: number | null | undefined, fractionDigits?: number): string;
  /** Importe en la moneda del tenant. */
  currency(value: number | null | undefined): string;
}

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Guión largo, no cadena vacía: una celda vacía se confunde con un bug. */
const NO_VALUE = '—';

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 86_400_000],
  ['month', 30 * 86_400_000],
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
  ['second', 1_000],
];

export function createFormatters(
  locale: string,
  timeZone: string,
  currency: string,
  now: () => number = Date.now,
): Formatters {
  // `Intl` lanza con un locale inválido; los datos ya vienen saneados de
  // `tenant/settings.ts`, pero un fallback acá evita que una configuración
  // rara deje el dashboard en blanco.
  const build = <T>(make: (loc: string) => T, fallbackLocale = 'es-PY'): T => {
    try {
      return make(locale);
    } catch {
      return make(fallbackLocale);
    }
  };

  const dateFmt = build(
    (loc) =>
      new Intl.DateTimeFormat(loc, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone,
      }),
  );
  const dateTimeFmt = build(
    (loc) =>
      new Intl.DateTimeFormat(loc, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone,
      }),
  );
  const timeFmt = build(
    (loc) =>
      new Intl.DateTimeFormat(loc, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone,
      }),
  );
  const expiryFmt = build(
    (loc) => new Intl.DateTimeFormat(loc, { month: '2-digit', year: 'numeric', timeZone: 'UTC' }),
  );
  const relativeFmt = build(
    (loc) => new Intl.RelativeTimeFormat(loc, { numeric: 'auto', style: 'short' }),
  );
  const compactFmt = build(
    (loc) => new Intl.NumberFormat(loc, { notation: 'compact', maximumFractionDigits: 1 }),
  );
  const currencyFmt = build(
    (loc) =>
      new Intl.NumberFormat(loc, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }),
  );
  const numberCache = new Map<number, Intl.NumberFormat>();
  const percentCache = new Map<number, Intl.NumberFormat>();

  const numberFmt = (digits: number): Intl.NumberFormat => {
    let cached = numberCache.get(digits);
    if (cached === undefined) {
      cached = build(
        (loc) =>
          new Intl.NumberFormat(loc, {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
          }),
      );
      numberCache.set(digits, cached);
    }
    return cached;
  };

  const percentFmt = (digits: number): Intl.NumberFormat => {
    let cached = percentCache.get(digits);
    if (cached === undefined) {
      cached = build(
        (loc) =>
          new Intl.NumberFormat(loc, {
            style: 'percent',
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
          }),
      );
      percentCache.set(digits, cached);
    }
    return cached;
  };

  const withDate =
    (formatter: Intl.DateTimeFormat) =>
    (value: string | number | Date | null | undefined): string => {
      const date = toDate(value);
      return date === null ? NO_VALUE : formatter.format(date);
    };

  return {
    locale,
    timeZone,
    date: withDate(dateFmt),
    dateTime: withDate(dateTimeFmt),
    time: withDate(timeFmt),
    expiry(value) {
      if (value === null || value === undefined || value === '') return NO_VALUE;
      // Un vencimiento es `AAAA-MM-DD` sin hora: interpretarlo en el huso
      // local lo correría un día en husos negativos.
      const date = toDate(`${value.slice(0, 10)}T00:00:00Z`);
      if (date === null) return NO_VALUE;
      // `month: '2-digit'` no alcanza: pedido junto con el año y sin día,
      // ICU degrada a numérico y enero sale `1/2027`. En una columna de
      // vencimientos eso son anchos distintos fila a fila, y sobre todo no es
      // como está impreso en el envase — GS1 `(17)` es AAMMDD, dos dígitos.
      // Se arma por partes para conservar el orden y el separador del locale.
      return expiryFmt
        .formatToParts(date)
        .map((part) => (part.type === 'month' ? part.value.padStart(2, '0') : part.value))
        .join('');
    },
    relative(value) {
      const date = toDate(value);
      if (date === null) return NO_VALUE;
      const delta = date.getTime() - now();
      for (const [unit, ms] of RELATIVE_UNITS) {
        if (Math.abs(delta) >= ms || unit === 'second') {
          return relativeFmt.format(Math.round(delta / ms), unit);
        }
      }
      return NO_VALUE;
    },
    number(value, fractionDigits = 0) {
      return value === null || value === undefined || !Number.isFinite(value)
        ? NO_VALUE
        : numberFmt(fractionDigits).format(value);
    },
    compact(value) {
      return value === null || value === undefined || !Number.isFinite(value)
        ? NO_VALUE
        : compactFmt.format(value);
    },
    percent(value, fractionDigits = 1) {
      return value === null || value === undefined || !Number.isFinite(value)
        ? NO_VALUE
        : percentFmt(fractionDigits).format(value);
    },
    currency(value) {
      return value === null || value === undefined || !Number.isFinite(value)
        ? NO_VALUE
        : currencyFmt.format(value);
    },
  };
}
